
import type {
  BaseErrorResponse,
  CreateDocumentScheduleReq,
  DocumentBatchCreateReq,
  DocumentBatchCreateRes,
  DocumentCreateReq,
  DocumentCreateRes,
  DocumentStateRes,
  EmptySuccessResponse,
  GetDocumentsRes,
  GetScheduledDocRes,
  GetScheduledDocsRes,
  UpdateDocumentScheduleReq,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function useDocument() {
  class Document<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags CPE3
     * @name DocumentControllerDocumentCreate
     * @summary Creating a document
     * @request POST:/api3/document/create
     * @secure
     * @response `200` `DocumentCreateRes`
     * @response `400` `BaseErrorResponse`
     */
    documentControllerDocumentCreate = (data: DocumentCreateReq, params: RequestParams = {}) =>
      this.request<DocumentCreateRes, BaseErrorResponse>({
        path: '/api3/document/create',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags CPE3
     * @name DocumentControllerDocumentBatchCreate
     * @summary Creating a batch of documents
     * @request POST:/api3/document/batch-create
     * @secure
     * @response `200` `DocumentBatchCreateRes`
     * @response `400` `BaseErrorResponse`
     */
    documentControllerDocumentBatchCreate = (data: DocumentBatchCreateReq, params: RequestParams = {}) =>
      this.request<DocumentBatchCreateRes, BaseErrorResponse>({
        path: '/api3/document/batch-create',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags CPE3
     * @name DocumentControllerGetDocuments
     * @summary Returns documents
     * @request GET:/api3/document
     * @secure
     * @response `200` `GetDocumentsRes`
     * @response `400` `BaseErrorResponse`
     */
    documentControllerGetDocuments = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example true */
        includeEmpty?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetDocumentsRes, BaseErrorResponse>({
        path: '/api3/document',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags CPE3
     * @name DocumentControllerGetDocumentStatus
     * @summary Returns document state
     * @request GET:/api3/document/state/{guid}
     * @secure
     * @response `200` `DocumentStateRes`
     * @response `400` `BaseErrorResponse`
     */
    documentControllerGetDocumentStatus = (guid: string, params: RequestParams = {}) =>
      this.request<DocumentStateRes, BaseErrorResponse>({
        path: `/api3/document/state/${guid}`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Document schedule
     * @name DocumentScheduleControllerGetScheduledDocs
     * @summary Getting the document schedule
     * @request GET:/api3/document/schedule
     * @secure
     * @response `200` `GetScheduledDocsRes`
     * @response `400` `BaseErrorResponse`
     */
    documentScheduleControllerGetScheduledDocs = (params: RequestParams = {}) =>
      this.request<GetScheduledDocsRes, BaseErrorResponse>({
        path: '/api3/document/schedule',
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Document schedule
     * @name DocumentScheduleControllerCreateScheduledDoc
     * @summary Creating the document schedule
     * @request POST:/api3/document/schedule
     * @secure
     * @response `200` `GetScheduledDocRes`
     * @response `400` `BaseErrorResponse`
     */
    documentScheduleControllerCreateScheduledDoc = (data: CreateDocumentScheduleReq, params: RequestParams = {}) =>
      this.request<GetScheduledDocRes, BaseErrorResponse>({
        path: '/api3/document/schedule',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Document schedule
     * @name DocumentScheduleControllerUpdateScheduledDoc
     * @summary Updating the document schedule
     * @request PUT:/api3/document/schedule
     * @secure
     * @response `200` `GetScheduledDocRes`
     * @response `400` `BaseErrorResponse`
     */
    documentScheduleControllerUpdateScheduledDoc = (data: UpdateDocumentScheduleReq, params: RequestParams = {}) =>
      this.request<GetScheduledDocRes, BaseErrorResponse>({
        path: '/api3/document/schedule',
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Document schedule
     * @name DocumentScheduleControllerDeleteScheduleDoc
     * @summary Deleting the document schedule
     * @request DELETE:/api3/document/schedule/{scheduleId}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    documentScheduleControllerDeleteScheduleDoc = (scheduleId: string, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/document/schedule/${scheduleId}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });
  }

  return new Document();
}
